import React from 'react';
import { Position, AbstractPureComponent } from '../../common';
import type { DownshiftProps, ChildrenFunction, ControllerStateAndHelpers } from 'downshift';
import Downshift from 'downshift';
import matchSorter from 'match-sorter';
import memoizeOne from 'memoize-one';
import type { IconSource } from '../IconV1';
import { IconV1 } from '../IconV1';
import { InputV1 } from '../InputV1';
import { generateComponentDisplayName, safeInvoke } from '../../utils';
import { PopoverV2 } from '../PopoverV2';
import { positionToPlacement } from '../PopoverV2/legacyPosition';
import { Container, ValueContainer, MenuContainer, ControllerButton, Menu, Toggle, Item, NoOptionMessage, Placeholder, ItemLabel } from './styles';
import { callAllHandlers } from '@shared/utils/functions';
import { SELECT_SELECTED_OPTION_CLASS } from './constants';
import { SELECT_CONTROLLED_INPUT_REQUIRES_ON_CHANGE, SELECT_CONTROLLED_VALUE_REQUIRES_ON_CHANGE } from '../../common/errors';

export type OptionType = {
  label: string;
  subtitle?: string;
  value: string;
  key?: string;
  disabled?: boolean;
  iconStart?: IconSource;
  iconEnd?: IconSource;
};
export type OptionsType<T> = ReadonlyArray<T>;

// type GroupedOptionsType<T> = {
//   options: ReadonlyArray<T>;
//   [key: string]: any;
// };

type ValueType<T> = T | OptionsType<T> | null | undefined;

type OptionFilter<T> = (option: T, inputValue: string, index: number) => boolean;
type LoadingMessage = string | ((inputValue: string | null) => string | JSX.Element);

export interface SelectV2Props<T = OptionType> {
  children?: never;

  /**
   * Close the select when the user selects an option,
   *
   * @default true
   */
  closeMenuOnSelect?: boolean;

  /**
   * Close the select when the user clears their selection.
   *
   * @default true
   */
  closeMenuOnClear?: boolean;

  /**
   * The value of `isOpen` when the selection is cleared or when an item is selected.
   *
   * @default false
   */
  defaultIsOpen?: boolean;

  defaultValue?: ValueType<T>;

  /**
   * Whether the select can be interacted with.
   *
   * @default false
   */
  disabled?: boolean;

  /**
   * Whether the Select list should take the full width of the input box.
   *
   * @default true;
   */
  fluid?: boolean;

  /**
   * Hide the selected options from the menu.
   *
   * @default false
   */
  hideSelectedOptions?: boolean;

  iconLeft?: IconSource;
  zIndex?: number;

  /**
   * Custom logic to determine if an option is disabled.
   */
  isOptionDisabled?: (option: T) => boolean;

  /** The initial `isOpen` value when initialized. */
  initialIsOpen?: boolean;

  /**
   * Initial value of inputValue when the select is initialized.
   */
  initialInputValue?: string;

  /**
   * The value of the search input. Creates a controlled component.
   *
   * @default undefined
   */
  inputValue?: string;

  /**
   * Whether the Select should show that it is currently loading data.
   *
   * Useful for when loading data asynchronously.
   *
   */
  loading?: boolean;

  /**
   * Message to display when loading options.
   *
   * @default 'Loading...'.
   */
  loadingMessage?: LoadingMessage;

  /**
   * Max height of the dropdown before scrolling.
   *
   * @default 300px
   */
  maxDropdownHeight?: string;

  /**
   * Supports multiple selections.
   *
   * @default false
   */
  multi?: boolean;

  /**
   * @default "No results."
   */
  noOptionsMessage?: React.ReactNode;

  /**
   * Array of options that populate the menu.
   */
  options: OptionsType<T>; // OptionsType<GroupedOptionsType<T>> | OptionsType<T>;

  onChange?: (value: T | null) => void;

  onInputChange?: (value: string) => void;

  onMenuOpen?: () => void;

  onMenuClose?: () => void;

  /**
   * A custom method to filter options to be displayed.
   */
  optionFilter?: OptionFilter<T>;

  placeholder?: React.ReactNode;

  /**
   * Defines whether search functionality is enabled.
   *
   * @default true
   */
  searchable?: boolean;

  /**
   * When enabled, free text entered into a searchable select (e.g. via browser
   * autofill) that exactly matches an option's label or value (case-insensitive)
   * is automatically committed through `onChange`. This bridges browser autofill,
   * which writes raw text into the underlying input rather than selecting an option.
   *
   * @default false
   */
  commitOnExactInputMatch?: boolean;

  value?: ValueType<T>;
  divideAt?: number;
  afterMenuContent?: React.ReactNode;

  iconRight?: React.ReactNode;

  isInvalid?: boolean;

  autoComplete?: string;

  /**
   * Id of the element that names this select. Downshift points the combobox, its input and its menu
   * at a label id whether or not one exists; without this that id is its own generated one, which
   * no element carries, so the field is announced without a name. Render the label yourself (a
   * visible `<label>`, or a `hidden` element when the name is for assistive tech only).
   */
  labelId?: string;

  /**
   * Defines The Popover position
   *
   */
  position?: Position;
  noBorder?: boolean;
  noBackground?: boolean;

  /**
   * Opt out of the field focus ring. Only for composites that supply their own focus indicator.
   *
   * @see InputV1Props['suppressFocusRing']
   */
  suppressFocusRing?: boolean;
}

type SelectState = {
  isOpen: boolean;
};

const selectDefaultProps = {
  closeMenuOnSelect: true,
  closeMenuOnClear: true,
  disabled: false,
  fluid: true,
  hideSelectedOptions: false,
  loadingMessage: 'Loading...' as LoadingMessage,
  maxDropdownHeight: '300px',
  noOptionsMessage: 'No results.',
  searchable: true,
};

type SelectInternalProps<T> = SelectV2Props<T> & typeof selectDefaultProps;

export class SelectV1<T extends OptionType> extends AbstractPureComponent<SelectInternalProps<T> & { 'data-testid'?: string }, SelectState> {
  static displayName = generateComponentDisplayName('Select');
  static defaultProps = selectDefaultProps;

  public inputRef: HTMLElement | null;
  protected toggleButtonRef: HTMLButtonElement | null = null;
  private menuContainerRef: HTMLElement | null;

  protected handleRefs = {
    inputRef: (node: HTMLInputElement | null) => {
      this.inputRef = node;
    },
    toggleButtonRef: (node: HTMLButtonElement | null) => {
      this.toggleButtonRef = node;
    },
    menuContainerRef: (node: HTMLDivElement | null) => {
      this.menuContainerRef = node;
    },
  };

  state: SelectState = {
    isOpen: this.props.defaultIsOpen || false,
  };

  // Stands in for PopoverV1's `onEntering`. PopoverV2 exposes no transition lifecycle, and the
  // menu is already in the DOM by the time this runs, which is all the focus + scroll below needs.
  componentDidUpdate(prevProps: SelectInternalProps<T> & { 'data-testid'?: string }, prevState: SelectState) {
    super.componentDidUpdate(prevProps, prevState);
    if (!prevState.isOpen && this.state.isOpen) {
      this.handlePopoverOnEntering();
    }
  }

  protected validateProps(props: SelectInternalProps<T>) {
    const { onInputChange, inputValue, value, onChange } = props;
    if (typeof inputValue !== 'undefined' && !onInputChange) {
      console.error(SELECT_CONTROLLED_INPUT_REQUIRES_ON_CHANGE);
    }

    if (value !== undefined && !onChange) {
      console.error(SELECT_CONTROLLED_VALUE_REQUIRES_ON_CHANGE);
    }
  }

  // ==============================================================
  // Handlers
  // ==============================================================

  /**
   * Handles Downshift state changes, particularly open/close state.
   * Subclasses can override to add custom state change handling.
   */
  protected handleDownshiftOnStateChange: DownshiftProps<T>['onUserAction'] = (options, stateAndHelpers) => {
    if (options.isOpen !== undefined && this.state.isOpen !== options.isOpen) {
      this.handleOnOpen(options.isOpen);
    }
  };

  /**
   * Handles selection changes and menu close behavior.
   * Subclasses can override to customize selection logic or prevent default behavior.
   */
  protected handleOnChange: DownshiftProps<T>['onChange'] = item => {
    const { closeMenuOnSelect, closeMenuOnClear, value, onChange } = this.props;

    if (value !== undefined && !onChange) {
      return;
    }

    const shouldClose = (item && closeMenuOnSelect) || (!item && closeMenuOnClear);

    if (shouldClose) {
      safeInvoke(this.props.onChange, item);
      this.handleOnOpen(false);
    } else {
      safeInvoke(this.props.onChange, item);
    }
  };

  /**
   * Handles input value changes for searchable selects.
   * Subclasses can override to add custom input handling (e.g., debouncing, async search).
   */
  protected handleOnInputValueChange: DownshiftProps<T>['onInputValueChange'] = (inputValue, stateAndHelpers) => {
    safeInvoke(this.props.onInputChange, inputValue);
    if (this.props.commitOnExactInputMatch && this.props.searchable && inputValue && !this.state.isOpen) {
      const normalized = inputValue.trim().toLowerCase();
      const match = (this.props.options as OptionsType<T>).find(option => option.label.toLowerCase() === normalized || String(option.value).toLowerCase() === normalized);
      if (match) {
        safeInvoke(this.props.onChange, match);
      }
    }
  };

  /**
   * Handles popover interaction (open/close via click).
   * Subclasses can override to customize popover interaction behavior.
   */
  protected handlePopoverOnInteraction = (isOpen: boolean) => {
    this.handleOnOpen(isOpen);
  };

  /**
   * Toggles the dropdown when the field itself is pressed.
   *
   * `PopoverV2` is driven with `triggerType="none"` here, so it wires up no click handling of its
   * own: its `click` trigger also takes over focus, moving it into the popover surface on open,
   * which would pull focus off the search input and make Downshift close the menu it just opened.
   */
  protected handleTriggerOnClick = (e: React.MouseEvent<HTMLElement>) => {
    if (this.props.disabled) {
      return;
    }
    // The chevron is decorative — the field is what opens the menu (see `Select.spec.tsx`). Under
    // PopoverV1 that fell out of a desync: a press on the chevron flipped this component's
    // `state.isOpen`, but Downshift immediately closed itself again, so the menu never appeared.
    // Nothing here reproduces that accident, so the exclusion has to be stated.
    if (this.toggleButtonRef?.contains(e.target as Node)) {
      return;
    }
    this.handleOnOpen(!this.state.isOpen);
  };

  /**
   * Handles popover entering animation, focuses input and scrolls to selected option.
   * Subclasses can override to customize focus or scroll behavior.
   */
  protected handlePopoverOnEntering = () => {
    if (this.inputRef) {
      this.inputRef.focus();
    }
    this.scrollSelectedOptionIntoView();
  };

  /**
   * Downshift state reducer for controlling state transitions.
   * Handles controlled value mode and maintains highlighted index on selection.
   * Subclasses can override to customize state transitions.
   */
  protected stateReducerHandler: DownshiftProps<T>['stateReducer'] = (state, changes) => {
    switch (changes.type) {
      case Downshift.stateChangeTypes.keyDownEnter:
      case Downshift.stateChangeTypes.clickItem: {
        if (this.props.value !== undefined) {
          return {
            ...state,
          };
        }
        // if (this.isInControlledValueMode()) {
        //   const { selectedItem, highlightedIndex } = state;
        //   const next = {
        //     ...changes,
        //     selectedItem,
        //     highlightedIndex
        //   };
        //   return next;
        // }
        return {
          ...changes,
          highlightedIndex: changes.selectedItem ? this.props.options.indexOf(changes.selectedItem) : null,
        };
      }

      default:
        return changes;
    }
  };

  private handleOnOpen = (isOpen: boolean) => {
    if (isOpen) {
      safeInvoke(this.props.onMenuOpen);
    } else {
      safeInvoke(this.props.onMenuClose);
    }
    this.setState({ isOpen });
  };

  // ==============================================================
  // Getters
  // ==============================================================

  /**
   * Converts an option to its display string for Downshift.
   * Subclasses can override to customize how options are stringified.
   */
  protected getItemDisplayValue = (item: T | null) => {
    return item ? item.label : '';
  };

  protected getOptions = ({ inputValue, selectedItem }: ControllerStateAndHelpers<T>) => {
    const { hideSelectedOptions, options, searchable } = this.props;

    const filteredOptions =
      searchable && inputValue
        ? // Filter for input
          this.filterOptions(inputValue)
        : hideSelectedOptions && selectedItem
          ? // Remove the selected item
            this.filterOptions(selectedItem.label)
          : // No filters applied, return all
            options;

    return filteredOptions;
  };

  protected isOptionDisabled = (option: T): boolean => {
    if (this.props.isOptionDisabled) {
      return this.props.isOptionDisabled(option);
    }
    return !!option.disabled;
  };

  // ==============================================================
  // Behaviour
  // ==============================================================

  private scrollSelectedOptionIntoView = () => {
    if (!this.menuContainerRef) {
      return;
    }
    const menu = this.menuContainerRef.firstChild as HTMLElement;
    if (!menu) {
      return;
    }
    const option = menu.getElementsByClassName(SELECT_SELECTED_OPTION_CLASS)[0] as HTMLElement | null;
    if (!option) {
      return;
    }

    const { scrollHeight: menuScrollHeight } = menu;
    const { clientHeight: optionHeight, offsetTop: optionOffsetTop } = option;

    const maxDropdownHeight = Number.parseInt(this.props.maxDropdownHeight, 10);
    if (menuScrollHeight > maxDropdownHeight) {
      menu.scrollTop = optionOffsetTop - optionHeight;
    }
  };

  // ==============================================================
  // Util
  // ==============================================================

  private filterOptions = (inputValue: string) => {
    const { optionFilter, options } = this.props;
    if (optionFilter) {
      return options.filter((option, index) => optionFilter(option, inputValue, index));
    }
    return matchSorter(options as any, inputValue, { keys: ['label'], threshold: matchSorter.rankings.MATCHES });
  };

  // ==============================================================
  // render
  // ==============================================================

  public render() {
    const { defaultIsOpen, initialIsOpen, initialInputValue, defaultValue, disabled, fluid, searchable, inputValue, value, position, noBackground, zIndex, labelId } = this.props;
    return (
      <Downshift
        labelId={labelId}
        defaultIsOpen={defaultIsOpen}
        initialIsOpen={initialIsOpen}
        initialInputValue={initialInputValue}
        initialSelectedItem={defaultValue as any}
        inputValue={searchable ? inputValue : ''}
        isOpen={this.state.isOpen}
        itemToString={this.getItemDisplayValue}
        onSelect={this.handleOnChange}
        onInputValueChange={this.handleOnInputValueChange}
        onUserAction={this.handleDownshiftOnStateChange}
        stateReducer={this.stateReducerHandler}
        selectedItem={value as any}
      >
        {
          (renderProps => {
            return (
              <Container
                noBackground={noBackground}
                fluid={fluid}
                {...renderProps.getRootProps({
                  refKey: 'ref',
                })}
              >
                <PopoverV2
                  isOpen={!disabled && renderProps.isOpen}
                  onOpen={this.handlePopoverOnOpen}
                  onClose={this.handlePopoverOnClose}
                  placement={positionToPlacement(position || Position.BOTTOM_LEFT)}
                  triggerType="none"
                  showArrow={false}
                  // Downshift's blur handling misses presses on surfaces that never take focus —
                  // the only close path for a non-searchable (readOnly) select.
                  closeOnOutsideClick
                  overrides={this.getPopoverOverrides(zIndex)}
                >
                  <PopoverV2.Trigger>{this.renderTrigger(renderProps)}</PopoverV2.Trigger>
                  <PopoverV2.Content>{this.renderMenu(renderProps)}</PopoverV2.Content>
                </PopoverV2>
              </Container>
            );
          }) as ChildrenFunction<T>
        }
      </Downshift>
    );
  }

  private handlePopoverOnOpen = () => this.handlePopoverOnInteraction(true);
  private handlePopoverOnClose = () => this.handlePopoverOnInteraction(false);

  /**
   * `zIndex` is a published prop, so it has to keep winning over the `popover` layer PopoverV2
   * puts on its root. `role` is forced to `presentation` because Downshift already marks the
   * option list up as a `listbox`; PopoverV2's default `dialog` would otherwise announce every
   * select on the site as a dialog wrapping a listbox.
   */
  private getPopoverOverrides = memoizeOne((zIndex: number | undefined) => ({
    Root: { props: { role: 'presentation', ...(zIndex === undefined ? {} : { zIndex }) } },
  }));

  /**
   * Renders the field that opens the dropdown. Wraps whatever `renderValue` produced rather than
   * baking the handler into it, so subclasses can override `renderValue` freely without having to
   * remember to re-attach the opener.
   */
  protected renderTrigger = (renderProps: ControllerStateAndHelpers<T>) => {
    const value = this.renderValue(renderProps) as React.ReactElement;
    return React.cloneElement(value, {
      onClick: callAllHandlers(value.props.onClick, this.handleTriggerOnClick),
    });
  };

  // \\\\\\\\\\\\\\\\\\\\\\\\
  // Value render
  // \\\\\\\\\\\\\\\\\\\\\\\\

  /**
   * Renders the value container (input + placeholder).
   * Override to customize the value display structure.
   */
  protected renderValue = (renderProps: ControllerStateAndHelpers<T>) => {
    return (
      <ValueContainer>
        {this.renderInput(renderProps)}
        {this.renderPlaceholder(renderProps)}
      </ValueContainer>
    );
  };

  private renderPlaceholder = ({ inputValue }: ControllerStateAndHelpers<T>) => {
    const { placeholder, searchable, iconLeft, value } = this.props;
    const inputIsEmpty = !inputValue;

    const selectedItem = Array.isArray(value) ? value[0].label : value;

    if (!!selectedItem) {
      return inputIsEmpty && searchable && <Placeholder hasLeftIcon={!!iconLeft}>{selectedItem.label}</Placeholder>;
    } else if (inputIsEmpty && placeholder) {
      return (
        <Placeholder
          hasLeftIcon={!!iconLeft}
          isPlaceholder={true}
        >
          {placeholder}
        </Placeholder>
      );
    }
    return null;
  };

  /**
   * Renders the input field with icons and toggle button.
   * Override to use a different input component or customize input behavior.
   */
  protected renderInput = (renderProps: ControllerStateAndHelpers<T>) => {
    const { disabled, searchable, iconLeft, iconRight, isInvalid, autoComplete, noBorder, suppressFocusRing, ...rest } = this.props;
    const iconMarkup = (
      <ControllerButton
        {...renderProps.getToggleButtonProps({ disabled })}
        ref={this.handleRefs.toggleButtonRef}
        onClick={null}
        // Not a separate tab stop. The chevron sits *inside* the field, and the field itself is
        // already focusable and already opens the menu, so this was a second stop on the same
        // control -- one that showed nothing, because the button set `outline: none`. The ARIA
        // combobox pattern excludes the popup toggle from the tab sequence for this reason.
        tabIndex={-1}
      >
        {searchable ? (
          <Toggle source="search" />
        ) : (
          <Toggle
            source="optionSelect"
            $rotate={renderProps.isOpen}
          />
        )}
      </ControllerButton>
    );
    return (
      <InputV1
        inputRef={this.handleRefs.inputRef}
        aria-invalid={isInvalid}
        {...renderProps.getInputProps({ disabled } as any)}
        readOnly={!searchable}
        value={searchable ? renderProps.inputValue : renderProps.selectedItem ? renderProps.selectedItem.label : ''}
        iconLeft={iconLeft}
        iconRight={iconRight ?? iconMarkup}
        autoComplete={autoComplete}
        data-testid={rest['data-testid']}
        minimal={noBorder}
        suppressFocusRing={suppressFocusRing}
      />
    );
  };

  // \\\\\\\\\\\\\\\\\\\\\\\\
  // Dropdown render
  // \\\\\\\\\\\\\\\\\\\\\\\\

  protected renderMenu = (renderProps: ControllerStateAndHelpers<T>) => {
    const { maxDropdownHeight, divideAt, afterMenuContent = undefined } = this.props;
    const { innerRef, ...menuProps } = renderProps.getMenuProps({ refKey: 'innerRef' }, { suppressRefError: true });
    return (
      <MenuContainer ref={this.handleRefs.menuContainerRef}>
        <Menu
          {...menuProps}
          ref={innerRef}
          maxHeight={maxDropdownHeight}
          divideAt={divideAt}
          className="select-menu"
        >
          {this.renderOptions(renderProps)}
        </Menu>
        {afterMenuContent}
      </MenuContainer>
    );
  };

  protected renderOptions = (renderProps: ControllerStateAndHelpers<T>) => {
    const { selectedItem, highlightedIndex, getItemProps, inputValue } = renderProps;
    const { noOptionsMessage, loading, loadingMessage } = this.props;

    if (loading) {
      return <NoOptionMessage>{typeof loadingMessage === 'function' ? loadingMessage(inputValue) : loadingMessage}</NoOptionMessage>;
    }

    const filteredOptions = this.getOptions(renderProps);
    if (filteredOptions.length) {
      return (filteredOptions as Array<any>).map((option, index) => {
        const isSelected = selectedItem === option;
        const isDisabled = this.isOptionDisabled(option);
        return (
          <Item
            key={option.key || option.value}
            isHighlighted={highlightedIndex === index}
            isSelected={isSelected}
            isDisabled={isDisabled}
            {...getItemProps({
              key: option.key || option.value,
              item: option,
              index,
              ['aria-selected']: isSelected,
              disabled: isDisabled,
            })}
            className={isSelected && SELECT_SELECTED_OPTION_CLASS}
          >
            {option.iconStart && <IconV1 source={option.iconStart} />}
            <ItemLabel
              variant="body1"
              color="currentColor"
              fontWeight={isSelected ? 700 : undefined}
            >
              {option.label}
            </ItemLabel>
            {option.iconEnd && <IconV1 source={option.iconEnd} />}
          </Item>
        );
      });
    }

    return <NoOptionMessage>{noOptionsMessage}</NoOptionMessage>;
  };
}
